import {useState} from 'react'
import * as Yup from 'yup'
import {Link} from 'react-router-dom'
import {useFormik} from 'formik'
import {requestPassword} from '../core/_requests'
import {Helmet} from 'react-helmet'
import Input from 'app/components/Input'
import Button from 'app/components/Button'

const initialValues = {
  email: 'admin@demo.com',
}

const forgotPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
})

export function ForgotPassword() {
  const [loading, setLoading] = useState(false)
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  const formik = useFormik({
    initialValues,
    validationSchema: forgotPasswordSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      setHasErrors(undefined)

      try {
        await requestPassword(values.email)
        setHasErrors(false)
      } catch (error) {
        setHasErrors(true)
        setStatus('The login detail is incorrect')
      } finally {
        setLoading(false)
        setSubmitting(false)
      }
    },
  })

  return (
    <>
      <Helmet>
        <title>SecureSlate</title>
      </Helmet>
      <form
        className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
        noValidate
        id='kt_login_password_reset_form'
        onSubmit={formik.handleSubmit}
      >
        <div className='text-center mb-10'>
          {/* begin::Title */}
          <h1 className='text-dark fw-bolder m-5'>Forgot Password ?</h1>
          {/* end::Title */}

          {/* begin::Link */}
          <div className='text-gray-500 fw-semibold fs-6'>
            Please enter the email address you used to login for
            <br />
            SecureSlate, and we'll send you a link to reset your password.
          </div>
          {/* end::Link */}
        </div>

        {hasErrors === true && (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>
              Sorry, looks like there are some errors detected, please try again.
            </div>
          </div>
        )}

        {hasErrors === false && (
          <div className='mb-10 p-8 rounded' style={{backgroundColor: '#EDFCF7', color: '#0E9F6E'}}>
            <div>
              Your password reset request has been sent. Please check your email for further
              instructions.
            </div>
          </div>
        )}

        {hasErrors === undefined && formik.values.email && (
          <div
            className='text-center mb-10 p-8 rounded'
            style={{backgroundColor: '#EDFCF7', color: '#0E9F6E'}}
          >
            {/* begin::Link */}
            <div className='fw-semibold fs-7'>
              If this email address is registered in your system,{' '}
              <strong> a password reset email </strong>should already be in your inbox.
            </div>
            {/* end::Link */}
          </div>
        )}

        {/* begin::Form group */}
        <div className='fv-row mb-8'>
          <Input
            type='email'
            label='Email'
            placeholder='Enter your email'
            className={`bg-transparent ${
              formik.touched.email && !formik.errors.email && 'is-valid'
            }`}
            labelClassName='fw-bolder'
            {...formik.getFieldProps('email')}
            error={formik.touched.email && formik.errors.email ? formik.errors.email : ''}
          />
        </div>
        {/* end::Form group */}

        {/* begin::Form group */}
        <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
          <Button
            type='submit'
            btnType='primary'
            id='kt_password_reset_submit'
            className='w-100'
            style={{
              height: '43.2px',
            }}
            data-kt-indicator={`${loading ? 'on' : ''}`}
            disabled={formik.isSubmitting}
          >
            <span className='indicator-label'>Send password reset instructions</span>
            {loading && (
              <span className='indicator-progress'>
                Please wait...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </Button>
        </div>
        <div className='mt-3'>
          <Link to='/auth/login'>
            <button
              type='button'
              id='kt_login_password_reset_form_cancel_button'
              className='btn p-0 m-0'
              disabled={formik.isSubmitting || !formik.isValid}
            >
              <img src='/media/buttons/backtologin.svg' alt='< Back to login' />
            </button>
          </Link>{' '}
        </div>
        {/* end::Form group */}
      </form>
    </>
  )
}
